/**
 * PATH: packages/bills/src/components/BillPanelChrome.tsx
 * FILE_ID: BILLS-PKG-CMP-CHROME-008
 * PURPOSE: Presentational chrome for the bill data panel — the status badge,
 *          the "received" metadata row, and the first-load skeleton.
 *
 * WHY: These are pure, bill-in / markup-out fragments with no panel state, so
 *      pulling them out of BillDataPanel.tsx keeps the panel focused on
 *      mutation logic. Extracted from BillDataPanel.tsx (P4-1 split) — no
 *      behaviour change.
 */

import { Mail } from 'lucide-react';
import { Badge, type BadgeTone } from '@quotech/ui';
import type { BillDetail } from '../types';
import { formatDateTime, formatRelative } from './format-datetime';

/**
 * Status → Badge tone. Single source of truth so the pill's colour reads the
 * same escalation everywhere: neutral (waiting) → info (AI done, review me) →
 * warning (needs an action) → success (in Xero) → danger (failed / held).
 * Falls back to neutral for any status not explicitly mapped.
 */
const STATUS_TONE: Partial<Record<BillDetail['status'], BadgeTone>> = {
  pending: 'neutral',
  extracted: 'info',
  reviewed: 'warning',
  pushed: 'success',
  failed: 'danger',
  quarantined: 'danger',
  awaiting_document: 'warning',
  needs_fetch: 'warning',
  needs_review: 'warning',
};

export function StatusBadge({ status }: { status: BillDetail['status'] }) {
  return (
    <Badge
      tone={STATUS_TONE[status] ?? 'neutral'}
      className="rounded-full px-2.5 py-0.5 text-[11px] font-medium capitalize"
    >
      {status}
    </Badge>
  );
}

/**
 * Receipt metadata row — date the email landed in the inbox + a
 * relative ("3 days ago") chip, so bookkeepers triaging dozens of
 * extracted bills can spot stale ones at a glance. The original
 * design only showed the absolute datetime once in tiny grey text;
 * users complained they had to mentally do arithmetic to gauge
 * urgency. Now it's a first-class row.
 */
export function ReceivedMetaRow({ bill }: { bill: BillDetail }) {
  const received = bill.email_received_at;
  if (!received) return null;
  const abs = formatDateTime(received);
  const rel = formatRelative(received);
  return (
    <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-[11px] text-muted-foreground">
      <span className="inline-flex items-center gap-1.5">
        <Mail className="h-3 w-3 shrink-0" aria-hidden />
        <span>Received {abs}</span>
      </span>
      {rel && (
        <span className="rounded-full bg-muted px-1.5 py-0.5 text-[10px] font-medium tabular-nums text-foreground/70">
          {rel}
        </span>
      )}
      {bill.email_subject && (
        <span className="w-full truncate italic text-muted-foreground/90" title={bill.email_subject}>
          “{bill.email_subject}”
        </span>
      )}
    </div>
  );
}

/**
 * Skeleton for the right-rail edit form on first load. Mirrors the real
 * layout — header block, action-button row, then a stack of labelled field
 * placeholders — so the rail doesn't jump when the bill resolves. Pure
 * presentational; announced politely once via an sr-only status label.
 */
export function BillFormSkeleton() {
  return (
    <div
      role="status"
      aria-label="Loading bill"
      className="flex h-full w-full flex-col overflow-hidden bg-muted/30 animate-pulse"
    >
      <span className="sr-only">Loading bill…</span>
      {/* Summary-card placeholder — monogram, identity, headline total. */}
      <div aria-hidden="true" className="shrink-0 space-y-3 px-4 pt-4 pb-1">
        <div className="flex items-center gap-3 rounded-lg border border-border bg-card p-3 shadow-sm">
          <div className="h-8 w-8 shrink-0 rounded-lg bg-muted" />
          <div className="flex min-w-0 flex-1 flex-col gap-1.5">
            <div className="h-4 w-40 rounded bg-muted" />
            <div className="h-3 w-28 rounded bg-muted/70" />
          </div>
          <div className="h-6 w-20 rounded bg-muted" />
        </div>
        {/* Action-bar placeholder. */}
        <div className="flex flex-wrap gap-2">
          <div className="h-8 w-20 rounded-md bg-muted" />
          <div className="h-8 w-24 rounded-md bg-muted/70" />
          <div className="h-8 w-20 rounded-md bg-muted/70" />
        </div>
      </div>
      {/* Field-group card placeholders. */}
      <div aria-hidden="true" className="flex flex-col gap-3 px-4 pb-4 pt-2">
        {Array.from({ length: 2 }).map((_, s) => (
          <div
            key={s}
            className="flex flex-col gap-3 rounded-lg border border-border bg-card p-3 shadow-sm"
          >
            <div className="h-3.5 w-24 rounded bg-muted/70" />
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="flex flex-col gap-1.5">
                <div className="h-2.5 w-16 rounded bg-muted/60" />
                <div className="h-9 w-full rounded-md bg-muted" />
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}
